import { MediaType } from '@pubstack/common/src/media-types'
import { cn } from '@pubstack/ui/utils/cn'
import { FunctionComponent } from 'react'
import { WithClassName } from '~/types/utils'
import { Icon } from './Icon'
import { Tooltip } from './Tooltip'

/**
 * `plain` is for reading, the other three are for picking.
 *
 * The circle is an affordance: it is what `BidderSelector` fills and empties as a media type is ticked
 * and unticked, so `primary` and `secondary` are the two faces of one control. Where nothing can be
 * clicked — a view flyout, a seat header — that ring spends a lot of ink saying "selected" about a list
 * with no unselected half, so `plain` draws the symbol alone, in the surrounding text colour.
 */
type Variant = 'primary' | 'secondary' | 'success' | 'plain'

const VARIANT_CLASS: Record<Variant, string> = {
  primary: 'bg-king border border-king text-white size-8 rounded-full',
  secondary: 'bg-white border border-king text-king size-8 rounded-full',
  success: 'bg-success border border-success text-white size-8 rounded-full',
  plain: 'text-current',
}

type Props = WithClassName & {
  mediaType: MediaType
  variant: Variant
  /**
   * How big the glyph is drawn, as a CSS length. Passed to `Icon` rather than set in `className`: `Icon`
   * is an Emotion-styled `span` that hard-sets its own `width`/`min-width`/`height`/`min-height` and
   * stretches the svg to `100%` of it, so a `[&_svg]:size-*` utility shrinks the svg inside a box that
   * never moves — and loses to the Emotion rule anyway, which is injected later at equal specificity.
   */
  iconSize?: string
}

/**
 * `Icon`'s own default, spelled out here rather than left implicit.
 *
 * The wrapper used to carry a `[&_svg]:size-5`, which never took effect: Emotion's rules are injected
 * unlayered and so outrank every Tailwind utility whatever its specificity — the same trap the "More"
 * control on `ModuleCard` documents. Every badge in the app has therefore been drawing at 24px, and this
 * keeps it that way for the callers that never asked otherwise.
 */
const DEFAULT_ICON_SIZE = '1.5rem'

export const MediaTypeBadge: FunctionComponent<Props> = ({ className, mediaType, variant, iconSize = DEFAULT_ICON_SIZE }) => (
  <Tooltip className={cn('flex shrink-0 items-center justify-center', VARIANT_CLASS[variant], className)} title={mediaType} containerStyle={{ textTransform: 'capitalize' }} offset={4}>
    <Icon name={mediaType} width={iconSize} height={iconSize} />
  </Tooltip>
)
